import { Button, DrawerBody, DrawerFooter, Flex, Spinner, Text, useToast } from "@chakra-ui/react"
import { DrawerContext } from "components/drawer"
import TickList from "components/select/TickList"
import { AuthContext } from "contexts/AuthContext"
import { getAll, updateMany } from "lib/firebase"
import { planCompositeMembership } from "lib/compositeMembership"
import { planAssignBroadcast } from "lib/membership"
import { isCompositeBroadcast } from "lib/playlist"
import { useContext, useEffect, useMemo, useState } from "react"

const KINDS = {
  group: {
    node: "group",
    noun: "groups",
    help: "Tick the groups that should play this broadcast. A group that plays another broadcast now (shown next to it) gets this one instead. A group always needs a broadcast, so the groups that already play this one stay as they are.",
  },
  location: {
    node: "location",
    noun: "locations",
    help: "Tick the locations that should play this broadcast instead of the broadcast of their group. A location that has another broadcast of its own gets this one instead. Unticking returns the location to the broadcast of its group.",
  },
  composite: {
    node: "broadcast",
    noun: "composite broadcasts",
    help: "Tick the composite broadcasts that should play this broadcast. It is added as the LAST item. Unticking removes it from the composite broadcast completely, every time it appears in the list (a multiplier copy of it too).",
  },
}

// Where one broadcast is used, and a way to change it. `kind` is "group", "location" or "composite".
// Saving writes everything in ONE update, from data read again right before writing.
export default function BroadcastLinks({ broadcast, kind, onChange }) {
  const { user } = useContext(AuthContext)
  const { closeDrawer } = useContext(DrawerContext)
  const toast = useToast()
  const config = KINDS[kind]

  const [state, setState] = useState({ loading: true, records: [], broadcasts: [], error: null })
  const [initial, setInitial] = useState(new Set())
  const [ticked, setTicked] = useState(new Set())
  const [saving, setSaving] = useState(false)

  // The broadcast and its multiplier copies: a composite broadcast may list a copy instead
  const family = (broadcasts) => new Set([broadcast.id, ...broadcasts.filter(b => b.sourceId === broadcast.id).map(b => b.id)])

  const holds = (record, broadcasts) => kind === "composite"
    ? (record.broadcastList || []).some(id => family(broadcasts).has(id))
    : record.broadcastId === broadcast.id

  useEffect(() => {
    let active = true
    Promise.all([getAll(config.node, user.clientId), getAll("broadcast", user.clientId)])
      .then(([records, broadcasts]) => {
        if (!active) return
        const candidates = kind === "composite" ? records.filter(b => !b.sourceId && isCompositeBroadcast(b)) : records
        const holding = new Set(candidates.filter(r => holds(r, broadcasts)).map(r => r.id))
        setState({ loading: false, records: candidates, broadcasts, error: null })
        setInitial(holding)
        setTicked(new Set(holding))
      })
      .catch(err => active && setState({ loading: false, records: [], broadcasts: [], error: err.message || String(err) }))
    return () => { active = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.clientId, broadcast.id, kind])

  const items = useMemo(() => {
    const nameOf = new Map(state.broadcasts.map(b => [b.id, b.name]))
    const fam = family(state.broadcasts)
    return state.records.map(record => {
      if (kind === "composite") {
        const times = (record.broadcastList || []).filter(id => fam.has(id)).length
        return { id: record.id, label: record.name || "", tag: times > 0 ? (times > 1 ? `x ${times}` : "in the list") : undefined }
      }
      const other = record.broadcastId && record.broadcastId !== broadcast.id ? nameOf.get(record.broadcastId) || "a deleted broadcast" : null
      const plays = record.broadcastId === broadcast.id
      // A group cannot be left without a broadcast, so one that plays this broadcast is locked
      const locked = kind === "group" && plays
      const none = kind === "group" ? "no broadcast" : "no broadcast of its own"
      const hint = plays ? (kind === "group" ? "plays this broadcast" : undefined) : (other ? `now plays: ${other}` : none)
      return { id: record.id, label: record.name || "", locked, hint }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, kind, broadcast.id])

  const toAdd = [...ticked].filter(id => !initial.has(id))
  const toRemove = [...initial].filter(id => !ticked.has(id))
  const changes = toAdd.length + toRemove.length

  const toggle = (id) => setTicked(current => {
    const next = new Set(current)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })
  const setMany = (ids, checked) => setTicked(current => {
    const next = new Set(current)
    ids.forEach(id => checked ? next.add(id) : next.delete(id))
    return next
  })

  const save = async () => {
    if (saving || changes === 0) return
    setSaving(true)
    try {
      let plan
      if (kind === "composite") {
        const [broadcasts, contentItems] = await Promise.all([getAll("broadcast", user.clientId), getAll("content", user.clientId)])
        plan = planCompositeMembership({ broadcasts, contentItems, broadcastId: broadcast.id, add: toAdd, remove: toRemove })
      } else {
        const records = await getAll(config.node, user.clientId)
        plan = planAssignBroadcast({ node: config.node, records, broadcastId: broadcast.id, add: toAdd, remove: toRemove })
      }
      if (Object.keys(plan.updates).length > 0) {
        await updateMany(plan.updates)
      }
      onChange && onChange()
      closeDrawer()
      const title = kind === "group"
        ? `Set on ${plan.added.length} group(s)`
        : `Added to ${plan.added.length}, removed from ${plan.removed.length} ${config.noun}`
      toast({ title, status: "success", duration: null, isClosable: true })
    } catch (err) {
      toast({ title: "Error!", description: err.message || String(err), status: "error", duration: null, isClosable: true })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <DrawerBody>
        {state.loading ? (
          <Flex justify="center" pt={8}><Spinner /></Flex>
        ) : state.error ? (
          <Text color="red.500">{state.error}</Text>
        ) : (
          <>
            <Text fontSize="sm" color="gray.500" mb={2}>{config.help}</Text>
            <TickList items={items} initial={initial} ticked={ticked} saving={saving} onToggle={toggle} onSetMany={setMany} noun={config.noun} />
          </>
        )}
      </DrawerBody>
      <DrawerFooter>
        <Button colorScheme="brand" borderRadius="30px" me="10px" isLoading={saving} isDisabled={state.loading || !!state.error || changes === 0} onClick={save}>
          Save{changes > 0 ? ` (${changes})` : ""}
        </Button>
      </DrawerFooter>
    </>
  )
}

